import { apiClient } from "@shared/lib/api-client";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { BotTypeOption, AiTimelineWithTrends, TimeRange, AiAnalyticsFilters, AiTimelineByIntentResponse } from "@saas/ai-analytics/lib/api";

/**
 * Fetches AI bot types for the marketing demo page
 * This is a server-side function that can be called from RSC
 * @param propertyApiKey - API key for authentication (bot types are generic but endpoint requires auth)
 */
export async function fetchAiBotTypesForDemo(propertyApiKey: string): Promise<BotTypeOption[]> {
  try {
    const response = await apiClient.analytics.ai["bot-types"].$get(
      {},
      {
        // Pass API key in header for server-side authentication
        headers: {
          "x-api-key": propertyApiKey,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch AI bot types: ${response.status}`);
    }

    const result = await response.json() as any;
    
    if (!result.success) {
      throw new Error("API response indicates failure");
    }

    return result.data;
  } catch (error) {
    console.error("Error fetching AI bot types for demo:", error);
    throw error;
  }
}

/**
 * Fetches AI timeline with trends data for the marketing demo page
 * This is a server-side function that can be called from RSC
 */
export async function fetchAiTimelineWithTrendsForDemo(
  propertyApiKey: string,
  timeRange: TimeRange,
  granularity: 'hour' | 'day' = 'day',
  filters?: AiAnalyticsFilters
): Promise<AiTimelineWithTrends> {
  try {
    const response = await apiClient.analytics.ai["timeline-with-trends"].$post(
      {
        json: {
          propertyApiKey: propertyApiKey,
          timeRange: {
            start: timeRange.start.toISOString(),
            end: timeRange.end.toISOString(),
          },
          granularity,
          filters: filters || {},
        },
      },
      {
        // Pass API key in header for server-side authentication
        headers: {
          "x-api-key": propertyApiKey,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch AI timeline with trends: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error("API response indicates failure");
    }

    return result.data;
  } catch (error) {
    console.error("Error fetching AI timeline with trends for demo:", error);
    throw error;
  }
}

/**
 * Fetches AI timeline by intent data for the marketing demo page
 * This is a server-side function that can be called from RSC
 */
export async function fetchAiTimelineByIntentForDemo(
  propertyApiKey: string,
  timeRange: TimeRange,
  granularity: 'hour' | 'day' = 'day',
  filters?: AiAnalyticsFilters
): Promise<AiTimelineByIntentResponse> {
  try {
    const response = await apiClient.analytics.ai["timeline-by-intent"].$post(
      {
        json: {
          propertyApiKey: propertyApiKey,
          timeRange: {
            start: timeRange.start.toISOString(),
            end: timeRange.end.toISOString(),
          },
          granularity,
          filters: filters || {},
        },
      },
      {
        // Pass API key in header for server-side authentication
        headers: {
          "x-api-key": propertyApiKey,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch AI timeline by intent: ${response.status}`);
    }

    const result = await response.json() as { success: boolean; data: AiTimelineByIntentResponse; error?: string };
    
    if (!result.success) {
      throw new Error(result.error || "Failed to fetch AI timeline by intent");
    }

    return result.data;
  } catch (error) {
    console.error("Error fetching AI timeline by intent for demo:", error);
    throw error;
  }
}

/**
 * Fetches all required AI analytics data for the marketing demo page
 * This combines both API calls and returns the data needed for the page
 */
export async function fetchDemoAiAnalyticsData(propertyApiKey: string) {
  try {
    // Get data for the last 7 days
    const end = new Date();
    const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    const timeRange: TimeRange = { start, end };

    // Fetch both datasets in parallel
    const [botTypes, timelineWithTrends] = await Promise.all([
      fetchAiBotTypesForDemo(propertyApiKey),
      fetchAiTimelineWithTrendsForDemo(propertyApiKey, timeRange, 'day'),
    ]);

    return {
      botTypes,
      timelineWithTrends,
    };
  } catch (error) {
    console.error("Error fetching demo AI analytics data:", error);
    throw error;
  }
} 

// React Query hooks for demo components

/**
 * Demo-specific hook for fetching AI bot types
 * @param propertyApiKey - API key for authentication
 */
export const useAiBotTypesDemo = (propertyApiKey: string) => {
  return useQuery({
    queryKey: ['demo', 'ai-bot-types', propertyApiKey],
    queryFn: () => fetchAiBotTypesForDemo(propertyApiKey),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since bot types don't change often
    enabled: !!propertyApiKey,
  });
};

/**
 * Demo-specific hook for fetching AI timeline with trends
 */
export const useAiTimelineWithTrendsDemo = (
  propertyApiKey: string,
  timeRange: TimeRange | 'live',
  granularity: 'hour' | 'day' = 'day',
  filters?: AiAnalyticsFilters
) => {
  return useQuery({
    queryKey: ['demo', 'ai-timeline-with-trends', propertyApiKey, timeRange, granularity, filters],
    queryFn: () => {
      if (timeRange === 'live') {
        // For live data, use the last 24 hours with hour granularity
        const end = new Date();
        const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
        return fetchAiTimelineWithTrendsForDemo(propertyApiKey, { start, end }, 'hour', filters);
      }
      return fetchAiTimelineWithTrendsForDemo(propertyApiKey, timeRange, granularity, filters);
    },
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    refetchInterval: timeRange === 'live' ? 30000 : false,
  });
};

/**
 * Demo-specific hook for fetching AI timeline by intent
 */
export const useAiTimelineByIntentDemo = (
  propertyApiKey: string,
  timeRange: TimeRange | 'live',
  granularity: 'hour' | 'day' = 'day',
  filters?: AiAnalyticsFilters
) => {
  return useQuery({
    queryKey: ['demo', 'ai-timeline-by-intent', propertyApiKey, timeRange, granularity, filters],
    queryFn: () => {
      if (timeRange === 'live') {
        // For live data, use the last 24 hours with hour granularity
        const end = new Date();
        const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
        return fetchAiTimelineByIntentForDemo(propertyApiKey, { start, end }, 'hour', filters);
      }
      return fetchAiTimelineByIntentForDemo(propertyApiKey, timeRange, granularity, filters);
    },
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    refetchInterval: timeRange === 'live' ? 30000 : false,
  });
}; 